// Chọn địa chỉ trên bản đồ — ghim cố định giữa bản đồ, kéo bản đồ (hoặc chạm vào một điểm)
// để đưa đúng chỗ vào dưới ghim, giống Google Maps / Grab.
//  · Có key Google Maps (GOOGLE_MAPS_API_KEY) → bản đồ, tìm kiếm, tra địa chỉ của Google.
//  · Không có / key lỗi → OpenStreetMap (Leaflet) + /geo/* (Goong nếu có GOONG_API_KEY, không thì Photon/Nominatim).

import React, { useEffect, useRef, useState, useCallback } from 'react'
import { createPortal } from 'react-dom'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { Search, X, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { get } from '@/lib/api'
import { toast } from 'sonner'
import { resolveAdministrativeAddress } from '@/lib/vietnamAddressResolver'
import type { OldArea } from '@shoptrack/common/admin-areas'
import { areaLabel } from './address/AreaPicker'
import {
  googleAutocomplete,
  googleMapsAuthFailed,
  googleReverseGeocode,
  googleSuggestionLocation,
  loadGoogleMaps,
  onGoogleMapsAuthFailure,
  type GoogleSuggestion,
} from '@/lib/googleMaps'

export interface MapPickedAddress {
  address: string
  ward: string
  district: string
  city: string
  cleanWard?: string
  cleanDistrict?: string
  cleanCity?: string
  formattedAddress: string
  lat: number
  lng: number
  /** Phường CŨ chứa điểm ghim (theo ranh giới cũ) — BEST chia đơn theo địa chỉ cũ; null = không xác định được */
  old?: OldArea | null
}

interface AddressMapPickerModalProps {
  open: boolean
  onClose: () => void
  onConfirm: (picked: MapPickedAddress) => void
  initialLat?: number | null
  initialLng?: number | null
  initialAddressText?: string
}

// Mặc định tọa độ trung tâm (Hà Nội: 21.0285, 105.8542)
const DEFAULT_CENTER: [number, number] = [21.0285, 105.8542]
const DEFAULT_ZOOM = 16

/** Rung phản hồi xúc giác nhẹ (Haptic Feedback) trên thiết bị di động */
const triggerHaptic = (duration = 15) => {
  if (typeof window !== 'undefined' && 'vibrate' in navigator) {
    try {
      navigator.vibrate(duration)
    } catch {
      // Bỏ qua nếu thiết bị hoặc trình duyệt không cho phép rung
    }
  }
}

/** Ghim cố định giữa bản đồ: mũi ghim là điểm được chọn; nhấc lên khi đang kéo bản đồ. */
function CenterPin({ lifted }: { lifted: boolean }) {
  return (
    <div className="pointer-events-none absolute left-1/2 top-1/2 z-20 -translate-x-1/2 -translate-y-full">
      <div className={`flex flex-col items-center transition-transform duration-150 ${lifted ? '-translate-y-3 scale-110' : ''}`}>
        <div className="flex items-center justify-center rounded-full bg-accent p-2 text-onAccent shadow-2xl ring-4 ring-white">
          <svg className="size-6 fill-white stroke-accent" viewBox="0 0 24 24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0" />
            <circle cx="12" cy="10" r="3" />
          </svg>
        </div>
      </div>
      <div className={`mx-auto mt-0.5 h-1.5 w-3.5 rounded-full bg-black/35 blur-[1px] transition-opacity ${lifted ? 'opacity-30' : 'opacity-80'}`} />
    </div>
  )
}

type MapEngine = 'pending' | 'google' | 'leaflet'

export function AddressMapPickerModal({
  open,
  onClose,
  onConfirm,
  initialLat,
  initialLng,
  initialAddressText,
}: AddressMapPickerModalProps) {
  const mapContainerRef = useRef<HTMLDivElement>(null)
  const mapInstanceRef = useRef<L.Map | null>(null)
  const gmapRef = useRef<google.maps.Map | null>(null)

  // Google Maps khi có key; không có / lỗi key → OpenStreetMap (Leaflet)
  const [engine, setEngine] = useState<MapEngine>('pending')
  const engineRef = useRef<MapEngine>(engine)
  engineRef.current = engine
  const [pinLifted, setPinLifted] = useState(false)
  // Bản đồ đã dựng xong (goTo dùng được) — để tự tìm địa chỉ đã nhập khi mở
  const [mapReady, setMapReady] = useState(false)
  const autoLocatedRef = useRef(false)

  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<any[]>([]) // GoogleSuggestion | kết quả /geo/autocomplete
  const [searching, setSearching] = useState(false)
  const [showDropdown, setShowDropdown] = useState(false)

  const [currentCoords, setCurrentCoords] = useState<{ lat: number; lng: number }>({
    lat: initialLat || DEFAULT_CENTER[0],
    lng: initialLng || DEFAULT_CENTER[1],
  })

  const [pickedData, setPickedData] = useState<MapPickedAddress | null>(null)
  const [customStreet, setCustomStreet] = useState('')
  const [loadingAddress, setLoadingAddress] = useState(false)
  const [isLocatingGPS, setIsLocatingGPS] = useState(false)

  const reverseGeocodeTimeoutRef = useRef<any>(null)
  /** Lần tra mới nhất — kéo bản đồ liên tục thì bỏ kết quả của lần tra cũ về muộn */
  const lookupSeqRef = useRef(0)
  const searchTimeoutRef = useRef<any>(null)
  // Điền tên kết quả vừa chọn vào ô tìm — không tìm lại (danh sách bật lại che bản đồ)
  const skipSearchRef = useRef(false)
  const settledRef = useRef('')
  // Địa chỉ của kết quả tìm vừa chọn — bản đồ tới đúng chỗ đó thì dùng luôn, không tra ngược
  // (tra ngược trả số nhà gần nhất, có thể khác cái người dùng vừa chọn)
  const presetRef = useRef<{ lat: number; lng: number; data: any } | null>(null)

  // ─── 1. Reverse Geocode tra cứu địa chỉ từ tọa độ ──────────────────
  const fetchAddressAtCoords = useCallback(async (lat: number, lng: number) => {
    const seq = ++lookupSeqRef.current
    setLoadingAddress(true)
    /** Phường cũ của điểm ghim (BEST chia đơn theo địa chỉ cũ) — kèm phường mới bản đồ trả để đoán khi thiếu ranh giới */
    const oldAt = (city?: string, ward?: string) => {
      const q = new URLSearchParams({ lat: String(lat), lng: String(lng) })
      if (city && ward) {
        q.set('city', city)
        q.set('ward', ward)
      }
      return get<{ old: OldArea | null }>(`/geo/old-area?${q}`)
        .then((r) => r?.old ?? null)
        .catch(() => null)
    }
    try {
      let data: any = null
      const preset = presetRef.current
      presetRef.current = null
      if (preset && Math.abs(preset.lat - lat) < 1e-4 && Math.abs(preset.lng - lng) < 1e-4) data = preset.data
      else if (engineRef.current === 'google') {
        data = await googleReverseGeocode(lat, lng).catch((err) => {
          console.warn('Google geocode error:', err)
          return undefined
        })
      }
      // Chưa có (OpenStreetMap, hoặc Google lỗi / không ra kết quả) → máy chủ tra
      if (!data) data = await get<any>(`/geo/reverse?lat=${lat}&lng=${lng}`)
      if (!data) throw new Error('no_address')
      const resolved = resolveAdministrativeAddress({
        city: data.city,
        district: data.district,
        ward: data.ward,
        street: data.address,
        fullAddress: data.formattedAddress,
      })
      const old = await oldAt(resolved.city || data.city, resolved.ward || data.ward)
      if (seq !== lookupSeqRef.current) return

      const streetVal = resolved.street || data.address || ''
      setCustomStreet(streetVal)

      setPickedData({
        address: streetVal,
        ward: resolved.ward || data.ward || '',
        district: resolved.district || data.district || '',
        city: resolved.city || data.city || '',
        cleanWard: resolved.cleanWard,
        cleanDistrict: resolved.cleanDistrict,
        cleanCity: resolved.cleanCity,
        formattedAddress: resolved.formattedAddress || data.formattedAddress || `${lat.toFixed(5)}, ${lng.toFixed(5)}`,
        lat,
        lng,
        old,
      })
    } catch (err) {
      console.warn('Reverse geocode error:', err)
      const old = await oldAt()
      if (seq !== lookupSeqRef.current) return
      setPickedData({
        address: '',
        ward: '',
        district: '',
        city: '',
        formattedAddress: `Tọa độ: ${lat.toFixed(5)}, ${lng.toFixed(5)}`,
        lat,
        lng,
        old,
      })
    } finally {
      if (seq === lookupSeqRef.current) setLoadingAddress(false)
    }
  }, [])

  // ─── 2. Bản đồ dừng → điểm giữa (dưới ghim) là vị trí chọn ─────────
  const settleAt = useCallback(
    (lat: number, lng: number) => {
      setPinLifted(false)
      const key = `${lat.toFixed(6)},${lng.toFixed(6)}`
      if (key === settledRef.current) return
      const first = !settledRef.current
      settledRef.current = key
      setCurrentCoords({ lat, lng })
      if (!first) triggerHaptic(15)
      if (reverseGeocodeTimeoutRef.current) clearTimeout(reverseGeocodeTimeoutRef.current)
      reverseGeocodeTimeoutRef.current = setTimeout(() => fetchAddressAtCoords(lat, lng), 250)
    },
    [fetchAddressAtCoords]
  )

  // ─── 3. Chọn Google Maps / OpenStreetMap mỗi lần mở ────────────────
  useEffect(() => {
    if (!open) return
    let alive = true
    if (googleMapsAuthFailed()) setEngine('leaflet')
    else loadGoogleMaps().then((ok) => alive && setEngine(ok ? 'google' : 'leaflet'))
    // Key bị Google từ chối (sai key / tên miền chưa cho phép) → quay về OpenStreetMap
    const off = onGoogleMapsAuthFailure(() => alive && setEngine('leaflet'))
    return () => {
      alive = false
      off()
    }
  }, [open])

  const initGoogleMap = useCallback(
    (el: HTMLDivElement, lat: number, lng: number) => {
      let disposed = false
      const listeners: google.maps.MapsEventListener[] = []
      ;(async () => {
        const { Map } = (await google.maps.importLibrary('maps')) as google.maps.MapsLibrary
        if (disposed) return
        const map = new Map(el, {
          center: { lat, lng },
          zoom: DEFAULT_ZOOM,
          disableDefaultUI: true,
          zoomControl: true,
          zoomControlOptions: { position: google.maps.ControlPosition.RIGHT_TOP }, // góc dưới để nút GPS
          gestureHandling: 'greedy',
          clickableIcons: false,
        })
        gmapRef.current = map
        setMapReady(true)
        listeners.push(
          map.addListener('dragstart', () => setPinLifted(true)),
          // Chạm / bấm vào đâu thì đưa chỗ đó về giữa (dưới ghim)
          map.addListener('click', (e: google.maps.MapMouseEvent) => e.latLng && map.panTo(e.latLng)),
          map.addListener('idle', () => {
            const c = map.getCenter()
            if (c) settleAt(c.lat(), c.lng())
          }),
        )
      })().catch((err) => {
        console.warn('Google Maps init error:', err)
        if (!disposed) setEngine('leaflet')
      })
      return () => {
        disposed = true
        listeners.forEach((l) => l.remove())
        gmapRef.current = null
        setMapReady(false)
      }
    },
    [settleAt]
  )

  const initLeafletMap = useCallback(
    (el: HTMLDivElement, lat: number, lng: number) => {
      const map = L.map(el, { center: [lat, lng], zoom: DEFAULT_ZOOM, zoomControl: false })
      L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19,
        attribution: '&copy; OpenStreetMap',
      }).addTo(map)
      L.control.zoom({ position: 'topright' }).addTo(map)
      mapInstanceRef.current = map
      setMapReady(true)

      map.on('dragstart', () => setPinLifted(true))
      map.on('click', (e: L.LeafletMouseEvent) => map.panTo(e.latlng))
      map.on('moveend', () => {
        const c = map.getCenter()
        settleAt(c.lat, c.lng)
      })
      settleAt(lat, lng)
      // Cập nhật lại kích thước phòng khi modal mở kèm hiệu ứng chuyển động
      const t = setTimeout(() => map.invalidateSize(), 200)
      return () => {
        clearTimeout(t)
        map.remove()
        mapInstanceRef.current = null
        setMapReady(false)
      }
    },
    [settleAt]
  )

  // ─── 4. Khởi tạo bản đồ ─────────────────────────────────────────────
  useEffect(() => {
    if (!open || engine === 'pending') return
    let cleanup: (() => void) | undefined

    // Timeout ngắn để đảm bảo modal DOM đã render đầy đủ kích thước
    const timer = setTimeout(() => {
      const el = mapContainerRef.current
      if (!el) return
      const startLat = initialLat || DEFAULT_CENTER[0]
      const startLng = initialLng || DEFAULT_CENTER[1]
      cleanup = engine === 'google' ? initGoogleMap(el, startLat, startLng) : initLeafletMap(el, startLat, startLng)
    }, 100)

    return () => {
      clearTimeout(timer)
      cleanup?.()
      settledRef.current = ''
      if (reverseGeocodeTimeoutRef.current) clearTimeout(reverseGeocodeTimeoutRef.current)
    }
  }, [open, engine, initialLat, initialLng, initGoogleMap, initLeafletMap])

  /** Đưa vị trí (kết quả tìm / GPS) về dưới ghim — bản đồ dừng thì tự tra địa chỉ. */
  const goTo = useCallback((lat: number, lng: number) => {
    const gmap = gmapRef.current
    if (engineRef.current === 'google' && gmap) {
      gmap.panTo({ lat, lng })
      if ((gmap.getZoom() ?? 0) < 17) gmap.setZoom(17)
      return
    }
    const map = mapInstanceRef.current
    map?.flyTo([lat, lng], Math.max(map.getZoom(), 17), { duration: 0.8 })
  }, [])

  // ─── 5. Tìm kiếm địa điểm với gợi ý Autocomplete ───────────────────
  /** Gợi ý cho một chuỗi: Google (khi có) → máy chủ /geo/autocomplete. */
  const searchPlaces = useCallback(async (query: string): Promise<any[]> => {
    // Ưu tiên kết quả gần chỗ đang xem
    const near = (() => {
      const g = gmapRef.current?.getCenter()
      if (engineRef.current === 'google' && g) return { lat: g.lat(), lng: g.lng() }
      const l = mapInstanceRef.current?.getCenter()
      return l ? { lat: l.lat, lng: l.lng } : undefined
    })()
    let results: any[] | null = null
    if (engineRef.current === 'google') {
      results = await googleAutocomplete(query, near).catch((err) => {
        console.warn('Google autocomplete error:', err)
        return null // lỗi (vd. chưa bật Places API) → tìm qua máy chủ
      })
    }
    if (!results) {
      const qs = new URLSearchParams({ q: query.slice(0, 250), ...(near ? { lat: String(near.lat), lng: String(near.lng) } : {}) })
      results = (await get<{ results: any[] }>(`/geo/autocomplete?${qs}`))?.results ?? []
    }
    return results
  }, [])

  useEffect(() => {
    if (skipSearchRef.current) {
      skipSearchRef.current = false
      return
    }
    if (!searchQuery.trim() || searchQuery.length < 2) {
      setSearchResults([])
      setShowDropdown(false)
      return
    }

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }

    searchTimeoutRef.current = setTimeout(async () => {
      setSearching(true)
      try {
        const results = await searchPlaces(searchQuery)
        setSearchResults(results)
        setShowDropdown(results.length > 0)
      } catch (err) {
        console.warn('Autocomplete error:', err)
      } finally {
        setSearching(false)
      }
    }, 350)

    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current)
    }
  }, [searchQuery, searchPlaces])

  /** Đưa một kết quả tìm kiếm về dưới ghim (kèm địa chỉ có sẵn của kết quả nếu có). */
  const moveToResult = useCallback(
    async (item: any) => {
      if ((item as GoogleSuggestion).prediction) {
        const loc = await googleSuggestionLocation(item as GoogleSuggestion)
        if (loc) goTo(loc.lat, loc.lng)
      } else if (item.lat && item.lng) {
        if (item.details) {
          const d = item.details
          presetRef.current = { lat: item.lat, lng: item.lng, data: { city: d.city, district: d.district, ward: d.ward, address: d.street, formattedAddress: item.address } }
        }
        goTo(item.lat, item.lng)
      } else if (item.id) {
        // Gợi ý Goong không kèm tọa độ — hỏi chi tiết
        const loc = await get<{ lat: number; lng: number; address?: any }>(`/geo/place?id=${encodeURIComponent(item.id)}`)
        if (loc.address) presetRef.current = { lat: loc.lat, lng: loc.lng, data: loc.address }
        goTo(loc.lat, loc.lng)
      }
    },
    [goTo]
  )

  const handleSelectSearchResult = async (item: any) => {
    setShowDropdown(false)
    skipSearchRef.current = true
    setSearchQuery(item.name || item.address)

    try {
      await moveToResult(item)
    } catch (err) {
      console.warn('Place location error:', err)
      toast.error('Không lấy được vị trí địa điểm này')
    }
  }

  // ─── 5b. Mở bản đồ khi đã có địa chỉ (chưa có tọa độ) → tới đúng chỗ đó ──
  // Thử cả chuỗi trước, không ra thì bớt dần phần đầu (số nhà → phường → quận) tới tỉnh.
  useEffect(() => {
    if (!open) {
      autoLocatedRef.current = false
      return
    }
    if (!mapReady || autoLocatedRef.current) return
    autoLocatedRef.current = true
    if (initialLat && initialLng) return
    const text = (initialAddressText || '').trim()
    if (text.length < 2) return

    let alive = true
    ;(async () => {
      const parts = text.split(',').map((p) => p.trim()).filter(Boolean)
      const attempts = [...new Set(parts.map((_, i) => parts.slice(i).join(', ')).filter((q) => q.length >= 2))].slice(0, 4)
      for (const q of attempts) {
        try {
          const results = await searchPlaces(q)
          if (!alive) return
          if (results[0]) {
            await moveToResult(results[0])
            return
          }
        } catch (err) {
          console.warn('Initial address lookup error:', err)
        }
      }
    })()
    return () => {
      alive = false
    }
  }, [open, mapReady, initialLat, initialLng, initialAddressText, searchPlaces, moveToResult])

  // ─── 6. Định vị GPS thiết bị hiện tại ──────────────────────────────
  const handleLocateCurrentGPS = () => {
    if (!navigator.geolocation) {
      toast.error('Trình duyệt của bạn không hỗ trợ định vị GPS')
      return
    }

    setIsLocatingGPS(true)
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setIsLocatingGPS(false)
        const { latitude, longitude } = pos.coords
        goTo(latitude, longitude)
        toast.success('Đã lấy vị trí hiện tại của bạn!')
      },
      (err) => {
        setIsLocatingGPS(false)
        if (err.code === 1) {
          toast.error('Chưa cấp quyền truy cập vị trí')
        } else {
          toast.error('Không thể lấy vị trí hiện tại')
        }
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    )
  }

  // ─── 7. Xác nhận địa chỉ đã chọn ──────────────────────────────────
  const handleConfirmPicked = () => {
    if (!pickedData) {
      toast.error('Đang tìm địa chỉ…')
      return
    }
    const finalStreet = customStreet.trim() || pickedData.address
    const fullFormatted = [
      finalStreet,
      pickedData.ward,
      pickedData.district,
      pickedData.city,
    ]
      .filter(Boolean)
      .join(', ')

    onConfirm({
      ...pickedData,
      address: finalStreet,
      formattedAddress: fullFormatted || pickedData.formattedAddress,
    })
    toast.success('Đã chọn vị trí từ bản đồ!')
    onClose()
  }

  /** Enter ở ô tìm: chọn kết quả đầu tiên (chưa có kết quả thì tìm ngay rồi chọn). */
  const handleSearchEnter = () => {
    if (showDropdown && searchResults[0]) {
      handleSelectSearchResult(searchResults[0])
      return
    }
    const q = searchQuery.trim()
    if (q.length < 2) return
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current)
    setSearching(true)
    searchPlaces(q)
      .then((results) => {
        if (results[0]) handleSelectSearchResult(results[0])
        else toast.info('Không tìm thấy địa điểm phù hợp')
      })
      .catch((err) => console.warn('Autocomplete error:', err))
      .finally(() => setSearching(false))
  }

  // Modal được dựng qua portal nhưng vẫn nằm trong <form> (tạo đơn, kho lấy hàng) theo cây React:
  // Enter / Esc xử lý tại đây và không để lọt ra ngoài — Enter không bao giờ gửi form bên dưới.
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault()
      e.stopPropagation()
      if (showDropdown) setShowDropdown(false)
      else onClose()
      return
    }
    if (e.key === 'Enter' && (e.target as HTMLElement).tagName === 'INPUT') {
      e.preventDefault()
      e.stopPropagation()
    }
  }

  if (!open || typeof document === 'undefined') return null

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Chọn trên bản đồ"
      onKeyDown={handleKeyDown}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-xs p-2 sm:p-4 animate-in fade-in-0 overscroll-contain"
    >
      <div className="relative w-full max-w-2xl h-[92dvh] sm:h-[85vh] max-h-[750px] bg-paper border border-rule rounded-xl shadow-2xl flex flex-col overflow-hidden overscroll-contain">
        {/* ── Header ────────────────────────────────────── */}
        <div className="px-4 py-3 border-b border-rule flex items-center justify-between bg-raise shrink-0">
          <h3 className="text-sm font-semibold text-ink">Chọn trên bản đồ</h3>

          <button
            type="button"
            onClick={onClose}
            className="p-1.5 text-muted hover:text-ink hover:bg-raiseSoft rounded-full transition-colors cursor-pointer"
          >
            <X className="size-5" />
          </button>
        </div>

        {/* ── Search Bar Floating Overlay ─────────────────── */}
        <div className="relative p-3 bg-raise/95 border-b border-rule shrink-0 z-10">
          <div className="relative flex items-center">
            <Search className="absolute left-3 size-4 text-muted pointer-events-none" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key !== 'Enter' || e.nativeEvent.isComposing) return
                e.preventDefault()
                handleSearchEnter()
              }}
              enterKeyHint="search"
              placeholder="Tìm kiếm địa điểm, số nhà, tên đường..."
              className="pl-9 pr-9 h-10 text-sm bg-paper border-rule focus-visible:ring-accent"
            />
            {searching ? (
              <Loader2 className="absolute right-3 size-4 animate-spin text-accent" />
            ) : searchQuery ? (
              <button
                type="button"
                onClick={() => {
                  setSearchQuery('')
                  setSearchResults([])
                  setShowDropdown(false)
                }}
                className="absolute right-3 p-0.5 text-muted hover:text-ink cursor-pointer"
              >
                <X className="size-4" />
              </button>
            ) : null}
          </div>

          {/* Floating Dropdown Results */}
          {showDropdown && searchResults.length > 0 && (
            <div className="absolute left-3 right-3 top-full mt-1 max-h-52 overflow-y-auto bg-paper border border-rule rounded-xl shadow-xl divide-y divide-rule/40 z-50">
              {searchResults.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => handleSelectSearchResult(item)}
                  className="w-full px-3.5 py-2.5 text-left text-xs hover:bg-raiseSoft flex items-start gap-2.5 transition-colors cursor-pointer"
                >
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-ink truncate">{item.name}</p>
                    <p className="text-xs text-muted truncate">{item.address}</p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* ── Map Canvas Container ────────────────────────── */}
        <div className="relative flex-1 w-full bg-muted/12 overflow-hidden select-none overscroll-contain">
          {/* key = engine: đổi Google ↔ OpenStreetMap thì dựng lại khung bản đồ sạch */}
          <div key={engine} ref={mapContainerRef} className="absolute inset-0 w-full h-full z-0 cursor-default" />

          {engine === 'pending' ? (
            <div className="absolute inset-0 z-10 flex items-center justify-center">
              <Loader2 className="size-5 animate-spin text-muted" />
            </div>
          ) : (
            <CenterPin lifted={pinLifted} />
          )}

          {/* 🎯 Nút GPS (Thumb Zone) */}
          <div className="absolute right-3 bottom-4 z-20">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={isLocatingGPS}
              onClick={handleLocateCurrentGPS}
              className="h-11 px-3.5 rounded-full bg-paper/95 backdrop-blur-xs border-rule shadow-lg text-xs font-semibold text-ink hover:bg-raise flex items-center gap-2 cursor-pointer"
            >
              <span>{isLocatingGPS ? 'Đang định vị…' : 'Vị trí hiện tại'}</span>
            </Button>
          </div>
        </div>

        {/* ── Bottom Address Card & Confirm Action (Thumb Friendly) ── */}
        <div className="p-3.5 sm:p-4 bg-raise border-t border-rule space-y-2.5 shrink-0 pb-safe">
          <div className="flex items-start gap-2.5">
            <div className="flex-1 min-w-0">
              <span className="text-xs text-muted font-medium">Vị trí đã ghim:</span>
              <p className="text-sm font-semibold text-ink leading-snug truncate">
                {pickedData?.formattedAddress || 'Đang tìm địa chỉ…'}
              </p>

              {/* Badges đầy đủ 4 cấp độ hành chính & chi tiết */}
              <div className="flex items-center gap-1.5 flex-wrap mt-1 text-xs">
                {customStreet && (
                  <span className="px-1.5 py-0.5 rounded bg-accent/10 border border-accent/30 text-accent font-medium flex items-center gap-1">
                    {customStreet}
                  </span>
                )}
                {pickedData?.ward && (
                  <span className="px-1.5 py-0.5 rounded bg-paper border border-rule font-medium text-inkSoft">
                    {pickedData.ward}
                  </span>
                )}
                {pickedData?.district && (
                  <span className="px-1.5 py-0.5 rounded bg-paper border border-rule font-medium text-inkSoft">
                    {pickedData.district}
                  </span>
                )}
                {pickedData?.city && (
                  <span className="px-1.5 py-0.5 rounded bg-paper border border-rule font-medium text-inkSoft">
                    {pickedData.city}
                  </span>
                )}
                <span className="font-mono text-xs text-muted/70 ml-auto">
                  {currentCoords.lat.toFixed(4)}, {currentCoords.lng.toFixed(4)}
                </span>
              </div>
              {pickedData?.old && (
                <p className="mt-1 text-xs text-muted">
                  BEST giao theo: <span className="text-ink">{areaLabel(pickedData.old)}</span>
                </p>
              )}
            </div>
          </div>

          {/* Ô xem & tinh chỉnh số nhà/địa chỉ chi tiết ngay trong modal */}
          <div className="flex items-center gap-2 pt-0.5">
            <label className="text-xs text-muted shrink-0 font-medium">Số nhà / đường:</label>
            <Input
              value={customStreet}
              onChange={(e) => setCustomStreet(e.target.value)}
              enterKeyHint="done"
              placeholder="Số nhà, ngõ, thôn xóm"
              className="h-8 text-xs bg-paper border-rule focus-visible:ring-accent"
            />
          </div>

          <div className="flex gap-2.5 pt-0.5">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="h-11 desktop:h-11 w-1/3 h-10 text-xs cursor-pointer"
            >
              Hủy
            </Button>
            <Button
              type="button"
              disabled={loadingAddress || !pickedData}
              onClick={handleConfirmPicked}
              className="h-11 desktop:h-11 w-2/3 h-10 text-xs font-semibold bg-accent text-onAccent hover:bg-accent/90 gap-1.5 shadow-sm cursor-pointer"
            >
              <span>Xác nhận</span>
            </Button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  )
}
